import React, {
  createContext, useContext, useState, ReactNode, useEffect,
  useMemo, useCallback,
} from 'react';
import { createTheme } from 'theme';
import { renderThemes, Themes } from 'theme/themes';
import { ThemeProvider } from '@mui/material/styles';
import { useAuthContext } from './auth-context';

interface ThemeContextType {
  themeType: Themes;
  setThemeType: (theme: Themes) => void;
  actualAppliedTheme: Themes.LIGHT | Themes.DARK;
  displayedTheme: Themes; // The theme that should be shown in the UI (matches what's actually applied)
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within a ThemeProvider');
  return context;
};

export const ThemeContextProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuthContext();

  // More reliable system theme detection
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(false);

  // Initialize theme state - prioritize user preference, fallback to SYSTEM
  const [themeType, setThemeType] = useState<Themes>(Themes.SYSTEM);

  // Detect system preference on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      setSystemPrefersDark(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setSystemPrefersDark(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    return undefined;
  }, []);

  // Set theme based on user data changes - this is critical for respecting user preferences
  useEffect(() => {
    if (user && user.theme !== undefined) {
      // Always sync the theme state with the user's preference when user data loads
      setThemeType(user.theme);
    }
  }, [user, user?.theme]);

  // Helper function to get resolved theme (never returns SYSTEM)
  const getResolvedTheme = useCallback((theme: Themes): Themes.LIGHT | Themes.DARK => {
    if (Number(theme) === Themes.SYSTEM) {
      // Use our reliable system detection
      const resolved = systemPrefersDark ? Themes.DARK : Themes.LIGHT;
      return resolved;
    }
    return theme as Themes.LIGHT | Themes.DARK;
  }, [systemPrefersDark]);

  // Calculate the actual theme that should be displayed
  // Priority: user.theme (if user is loaded) > themeType state > SYSTEM default
  const getActualTheme = useCallback((): Themes.LIGHT | Themes.DARK => {
    // Always use the current themeType state which gets updated when user data loads
    const resolved = getResolvedTheme(themeType);
    return resolved;
  }, [themeType, getResolvedTheme]);

  const actualTheme = getActualTheme();

  // Calculate what theme should be displayed in the UI
  // This should match the user's actual preference when available
  const displayedTheme = useMemo(() => {
    if (user && user.theme !== undefined) {
      return user.theme;
    }
    return themeType;
  }, [user, themeType]);

  // Create theme colors based on the actual resolved theme
  const themeColors = useMemo(() => {
    const currentActualTheme = getActualTheme();

    const lightColors = {
      '--side-nav-color': renderThemes(Themes.LIGHT).sidenav_color,
      '--data-table-header': renderThemes(Themes.LIGHT).data_table_header,
      '--data-table-body': renderThemes(Themes.LIGHT).data_table_body,
      '--data-table-rollover': renderThemes(Themes.LIGHT).data_table_rollover,
      '--calendar-current-day': renderThemes(Themes.LIGHT).calendar_current_day,
      '--calendar-weekend': renderThemes(Themes.LIGHT).calendar_weekend,
      '--top-nav-text-color': renderThemes(Themes.LIGHT).top_nav_text_color,
      '--calendar-main': renderThemes(Themes.LIGHT).calendar_main,
      '--calendar-line': renderThemes(Themes.LIGHT).calendar_line,
      '--calendar-week-heading': renderThemes(Themes.LIGHT).calendar_week_heading,
      '--calendar-text': renderThemes(Themes.LIGHT).calendar_text,
      '--calendar-text-hover': renderThemes(Themes.LIGHT).calendar_text_hover,
      '--impersonate-color': renderThemes(Themes.LIGHT).impersonate_color,
      '--primary-color': renderThemes(Themes.LIGHT).primary_component.main,
      '--dialog-background': renderThemes(Themes.LIGHT).dialog_background,
      '--tooltip-color': renderThemes(Themes.LIGHT).tooltip_color,
      '--dialog-text': renderThemes(Themes.LIGHT).dialog_text,
      '--dialog-title': renderThemes(Themes.LIGHT).dialog_title,
      '--disabled-color': renderThemes(Themes.LIGHT).disabled_color,
      '--checkpoint-question': renderThemes(Themes.LIGHT).checkpoint_question,
      '--contained-text': renderThemes(Themes.LIGHT).contained_text,
      '--primary-component-light': renderThemes(Themes.LIGHT).primary_component.light,
      '--primary-component-main': renderThemes(Themes.LIGHT).primary_component.main,
      '--primary-component-dark': renderThemes(Themes.LIGHT).primary_component.dark,
      '--success-light': renderThemes(Themes.LIGHT).success.light,
      '--success-main': renderThemes(Themes.LIGHT).success.main,
      '--success-dark': renderThemes(Themes.LIGHT).success.dark,
      '--info-light': renderThemes(Themes.LIGHT).info.light,
      '--info-main': renderThemes(Themes.LIGHT).info.main,
      '--info-dark': renderThemes(Themes.LIGHT).info.dark,
      '--warning-light': renderThemes(Themes.LIGHT).warning.light,
      '--warning-main': renderThemes(Themes.LIGHT).warning.main,
      '--warning-dark': renderThemes(Themes.LIGHT).warning.dark,
      '--error-light': renderThemes(Themes.LIGHT).error.light,
      '--error-main': renderThemes(Themes.LIGHT).error.main,
      '--error-dark': renderThemes(Themes.LIGHT).error.dark,
    };

    const darkColors = {
      '--side-nav-color': renderThemes(Themes.DARK).sidenav_color,
      '--data-table-header': renderThemes(Themes.DARK).data_table_header,
      '--data-table-body': renderThemes(Themes.DARK).data_table_body,
      '--data-table-rollover': renderThemes(Themes.DARK).data_table_rollover,
      '--top-nav-text-color': renderThemes(Themes.DARK).top_nav_text_color,
      '--calendar-current-day': renderThemes(Themes.DARK).calendar_current_day,
      '--calendar-weekend': renderThemes(Themes.DARK).calendar_weekend,
      '--calendar-main': renderThemes(Themes.DARK).calendar_main,
      '--calendar-line': renderThemes(Themes.DARK).calendar_line,
      '--calendar-week-heading': renderThemes(Themes.DARK).calendar_week_heading,
      '--calendar-text': renderThemes(Themes.DARK).calendar_text,
      '--calendar-text-hover': renderThemes(Themes.DARK).calendar_text_hover,
      '--impersonate-color': renderThemes(Themes.DARK).impersonate_color,
      '--primary-color': renderThemes(Themes.DARK).primary_component.main,
      '--dialog-background': renderThemes(Themes.DARK).dialog_background,
      '--dialog-text': renderThemes(Themes.DARK).dialog_text,
      '--dialog-title': renderThemes(Themes.DARK).dialog_title,
      '--tooltip-color': renderThemes(Themes.DARK).tooltip_color,
      '--disabled-color': renderThemes(Themes.DARK).disabled_color,
      '--checkpoint-question': renderThemes(Themes.DARK).checkpoint_question,
      '--contained-text': renderThemes(Themes.DARK).contained_text,
      '--primary-component-light': renderThemes(Themes.DARK).primary_component.light,
      '--primary-component-main': renderThemes(Themes.DARK).primary_component.main,
      '--primary-component-dark': renderThemes(Themes.DARK).primary_component.dark,
      '--success-light': renderThemes(Themes.DARK).success.light,
      '--success-main': renderThemes(Themes.DARK).success.main,
      '--success-dark': renderThemes(Themes.DARK).success.dark,
      '--info-light': renderThemes(Themes.DARK).info.light,
      '--info-main': renderThemes(Themes.DARK).info.main,
      '--info-dark': renderThemes(Themes.DARK).info.dark,
      '--warning-light': renderThemes(Themes.DARK).warning.light,
      '--warning-main': renderThemes(Themes.DARK).warning.main,
      '--warning-dark': renderThemes(Themes.DARK).warning.dark,
      '--error-light': renderThemes(Themes.DARK).error.light,
      '--error-main': renderThemes(Themes.DARK).error.main,
      '--error-dark': renderThemes(Themes.DARK).error.dark,
    };

    return currentActualTheme === Themes.DARK ? darkColors : lightColors;
  }, [getActualTheme]);

  // Apply CSS variables whenever the actual theme changes
  useEffect(() => {
    Object.entries(themeColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, [themeColors]);

  const contextValue = useMemo(() => ({
    themeType,
    setThemeType,
    actualAppliedTheme: actualTheme,
    displayedTheme,
  }), [themeType, actualTheme, displayedTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={createTheme(getActualTheme())}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
